// Import flow (F1.1): hash → this session's songs → decode → analyze in a worker.
// Nothing is stored (ADR 0007): the song, its chords and its audio stay in memory for this tab.
import { ANALYSIS_SAMPLE_RATE } from '@/core/analysis/params';
import type { AnalysisResult, AnalysisStage } from '@/core/analysis/types';
import { getAudioContext } from '@/audio/context';
import { decodeAudio, makeAnalysisInput } from '@/audio/decode';
import { sha256Hex } from '@/audio/hash';
import { rememberDecoded, rememberFile } from '@/audio/sessionAudio';
import { getAnalysis, getSong, markOpened, putAnalysis, putSong } from '@/storage/songsRepo';
import type { Song } from '@/storage/types';
import { useSettingsStore } from '@/state/settingsStore';
import { AnalysisCancelledError, startAnalysis, type AnalysisJob } from '@/workers/analysisClient';
import { readTags, type FileTags } from './readTags';
import {
  checkDuration,
  checkFileBeforeReading,
  decodeErrorMessage,
  mimeTypeOf,
  titleFromFileName,
} from './validate';

/** 'model': the BTC chord model (ADR 0009), only when it's used. */
export type ImportStep = 'decoding' | 'model' | 'tuning' | 'beats' | 'chords' | 'key';

export interface ImportProgress {
  step: ImportStep;
  /** Overall 0–1: decoding is the first 12%, analysis the rest. */
  fraction: number;
}

export interface ImportFileInfo extends FileTags {
  songId: string;
  fileName: string;
  fileSize: number;
  duration?: number;
}

export interface ImportOutcome {
  song: Song;
  result: AnalysisResult;
  /** Loaded from the library without analyzing again. */
  fromCache: boolean;
}

export interface ImportHandle {
  outcome: Promise<ImportOutcome>;
  cancel(): void;
}

export interface ImportOptions {
  onProgress?: (progress: ImportProgress) => void;
  onFileInfo?: (info: ImportFileInfo) => void;
}

/** A failure to show to the user as is. */
export class ImportError extends Error {
  override name = 'ImportError';
}

const DECODE_SHARE = 0.12;

const STEP_OF_STAGE: Record<AnalysisStage, ImportStep> = {
  model: 'model',
  tuning: 'tuning',
  beats: 'beats',
  spectrum: 'chords',
  harmonics: 'chords',
  chords: 'chords',
  key: 'key',
};

export function startImport(file: File, options: ImportOptions = {}): ImportHandle {
  let cancelled = false;
  let job: AnalysisJob | null = null;
  const assertActive = () => {
    if (cancelled) throw new AnalysisCancelledError();
  };

  const run = async (): Promise<ImportOutcome> => {
    const problem = checkFileBeforeReading(file);
    if (problem) throw new ImportError(problem);
    options.onProgress?.({ step: 'decoding', fraction: 0 });

    const bytes = await file.arrayBuffer();
    const songId = await sha256Hex(bytes);
    assertActive();
    options.onFileInfo?.({ songId, fileName: file.name, fileSize: file.size });

    const existing = await getSong(songId);
    const cachedResult = existing ? await getAnalysis(songId) : undefined;
    if (existing && cachedResult) {
      options.onFileInfo?.({
        songId,
        fileName: file.name,
        fileSize: file.size,
        duration: existing.duration,
        title: existing.title,
        artist: existing.artist,
        cover: existing.cover,
      });
      rememberFile(songId, bytes);
      await markOpened(songId);
      options.onProgress?.({ step: 'key', fraction: 1 });
      return { song: (await getSong(songId)) ?? existing, result: cachedResult, fromCache: true };
    }

    // Tags are read alongside decoding and analysis; they never delay or fail the import.
    const info: ImportFileInfo = { songId, fileName: file.name, fileSize: file.size };
    const tagsRead = readTags(file).then((tags) => {
      Object.assign(info, tags);
      options.onFileInfo?.({ ...info });
      return tags;
    });

    const buffer = await decodeOrFail(bytes, file.name);
    assertActive();
    const durationProblem = checkDuration(buffer.duration);
    if (durationProblem) throw new ImportError(durationProblem);
    info.duration = buffer.duration;
    options.onFileInfo?.({ ...info });
    rememberFile(songId, bytes);
    rememberDecoded(songId, buffer);
    const samples = await makeAnalysisInput(buffer);
    assertActive();
    // Settings → Chord recognition (ADR 0009). With BTC, its step runs first.
    const model = useSettingsStore.getState().chordModel === 'btc' ? 'btc' : 'dsp';
    options.onProgress?.({ step: model === 'btc' ? 'model' : 'tuning', fraction: DECODE_SHARE });

    job = startAnalysis(samples, ANALYSIS_SAMPLE_RATE, {
      model,
      onProgress: ({ stage, fraction }) => {
        options.onProgress?.({
          step: STEP_OF_STAGE[stage],
          fraction: DECODE_SHARE + (1 - DECODE_SHARE) * fraction,
        });
      },
    });
    const { result } = await job.result;
    assertActive();

    const tags = await tagsRead;
    const now = Date.now();
    const song: Song = {
      id: songId,
      title: tags.title ?? existing?.title ?? titleFromFileName(file.name),
      fileName: file.name,
      fileSize: file.size,
      mimeType: mimeTypeOf(file),
      duration: result.duration,
      summary: {
        key: result.key,
        bpm: result.beatGrid.bpm,
        chordChanges: result.segments.filter((s) => s.chord).length,
      },
      createdAt: existing?.createdAt ?? now,
      lastOpenedAt: now,
    };
    const artist = tags.artist ?? existing?.artist;
    if (artist) song.artist = artist;
    const cover = tags.cover ?? existing?.cover;
    if (cover) song.cover = cover;
    await putSong(song);
    await putAnalysis(songId, result);
    return { song, result, fromCache: false };
  };

  return {
    outcome: run(),
    cancel() {
      cancelled = true;
      job?.cancel();
    },
  };
}

async function decodeOrFail(bytes: ArrayBuffer, fileName: string): Promise<AudioBuffer> {
  try {
    return await decodeAudio(getAudioContext(), bytes);
  } catch {
    throw new ImportError(decodeErrorMessage(fileName));
  }
}
